import { useEffect, useState } from 'react';

import { getTextFromPdf } from 'utilsV2';
import { useClaudeMessages } from 'hooksv2/useAnthropic';

import { AskPdfProps, ConversationProps, Tabs } from '..';

enum Prompts {
  OVERVIEW = 'Summarize the main ideas and key insights of this PDF in a clear and concise way. Return between 5 and 10 well-structured lines as an HTML string (use <p> and <ul>/<li> where appropriate). Do not include any extra text outside of the HTML.',
  QUESTIONS = 'Generate an array of 12 thoughtful and relevant PDF-related questions that a prospective customer or stakeholder might have after reading the provided PDF. Prioritize the questions in order of likely popularity or relevance based on common customer concerns and buying behavior. Return exactly an array of 12 strings, no additional text or formatting.',
  FAQ = 'Extract the top 10 frequently asked questions (FAQ) from this PDF along with clear and informative answers. Return exactly an array of objects in the following format: [{ question: "", answer: "" }]. Each answer must be at least 2 complete sentences. No additional text or formatting.'
}

export const useAskPdf = ({ url }: Partial<AskPdfProps>) => {
  const [fileText, setFileText] = useState('');
  const [faq, setFaq] = useState<
    {
      question: string;
      answer: string;
    }[]
  >([]);
  const [overview, setOverview] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<Tabs>(Tabs.AskT);
  const [initLoading, setInitLoading] = useState(true);
  const [questions, setQuestions] = useState<string[]>([]);

  const [conversation, setConversation] = useState<ConversationProps[]>([]);

  const props = {
    tokens: 3024,
    stream: false,
    fileBase: fileText
  };

  const { stream, asyncResponse } = useClaudeMessages({});
  const { stream: questionsStream } = useClaudeMessages({
    prompt: Prompts.QUESTIONS,
    ...props
  });
  const { stream: faqStream } = useClaudeMessages({
    ...props,
    prompt: Prompts.FAQ
  });
  const { stream: overviewStream } = useClaudeMessages({
    ...props,
    prompt: Prompts.OVERVIEW
  });

  const tabsList = Object.values(Tabs)?.map(v => ({
    label: v,
    value: v
  }));

  const returnParseArray = (val: string) => {
    const start = val.indexOf('['); // Find the index where the array starts
    const end = val.indexOf(']'); // Find the index where the array ends

    if (!end || end === -1) return '';

    const arrayString = val.substring(start, end + 1); // Extract the array string

    return JSON.parse(arrayString);
  };

  const getBase64 = async (newUrl: string) => {
    const res = await getTextFromPdf(newUrl);
    setFileText(res);
  };

  useEffect(() => {
    if (url) getBase64(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const onSubmit = async (val: string) => {
    setLoading(true);
    setConversation(prev => [
      ...prev,
      {
        question: val,
        answer: ''
      }
    ]);
    asyncResponse({
      asHtml: true,
      fileBase: fileText,
      tokens: 5024,
      prompt: val
    });
  };

  useEffect(() => {
    if (stream) {
      if (loading) setLoading(false);
      const lastConvo = conversation?.[conversation?.length - 1];
      const convo = {
        question: lastConvo?.question,
        answer: stream
      };

      setConversation(prev => prev.slice(0, -1).concat(convo));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream]);

  useEffect(() => {
    if (questionsStream) {
      try {
        const res = returnParseArray(questionsStream);
        setQuestions(prev => res || prev);
        if (res?.length) setInitLoading(false);
        // eslint-disable-next-line no-empty
      } catch (error) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsStream]);

  useEffect(() => {
    if (faqStream) {
      try {
        const res = returnParseArray(faqStream);
        setFaq(prev => res || prev);
        // eslint-disable-next-line no-empty
      } catch (error) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faqStream]);

  useEffect(() => {
    if (overviewStream) {
      setOverview(overviewStream);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewStream]);

  return {
    tab,
    faq,
    loading,
    tabsList,
    overview,
    questions,
    initLoading,
    conversation,

    setTab,
    onSubmit
  };
};
